import React, { ReactNode, useCallback, useMemo } from 'react';

import { Box, Text } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';

import ActionButton, { ActionLink } from 'components/action-button';
import Modal, { ModalContent } from 'components/modal';
import OrderIssues from 'pages/order-confirmation/order-issues';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useUIContext } from 'state/ui';
import { theme } from 'styles/configure-theme';
import { routes } from 'utils/routing';

import HeadingContainer from './cart-error-heading-wrapper';

const ButtonGutter = Box.withConfig({
  flexDirection: { base: 'column', md: 'row' },
  justifyContent: { base: 'center', md: 'space-between' },
  marginTop: '$8',
});

const Body = Text.withConfig({
  marginX: 0,
  marginTop: 8,
  textAlign: 'justify',
  fontSize: 'md',
});

const Disclaimer = Text.withConfig({
  variant: 'copyTwo',
  maxWidth: '$64',
  textAlign: 'center',
  marginTop: '$8',
});

const LinkWrapper = Box.withConfig({
  textAlign: 'center',
});

interface ICartError {
  title?: string;
  body?: ReactNode;
  disclaimer?: string;
  onAccept?: () => void;
  onDismiss?: () => void;
  additionalLoggingContext: Object;
}

const message = 'Cart Error: Order could not be completed';

const CartError: React.FC<React.PropsWithChildren<ICartError>> = ({
  title,
  body,
  disclaimer,
  onAccept,
  onDismiss,
  additionalLoggingContext,
}) => {
  const isShowingOrderingIssue = useFlag(LaunchDarklyFlag.SHOW_ORDERING_ISSUE);
  const { shouldConfirmStoreOnCheckout } = useUIContext();

  const router = useRouter();

  const startOverCallback = useCallback(() => {
    router.replace({ pathname: routes.storeLocator, params: { redirectUrl: routes.cart } });
    shouldConfirmStoreOnCheckout(true);
  }, [shouldConfirmStoreOnCheckout, router]);

  const modalHeading = title || t('restaurantUnavailableErrorTitle');
  const modalBody = body || t('restaurantUnavailableErrorMessage');

  return (
    <Modal
      additionalLoggingContext={useMemo(
        () => ({
          ...additionalLoggingContext,
          guestMessageTitle: title,
          isOrderCouldNotBeCompletedError: true,
        }),
        [additionalLoggingContext, title]
      )}
      onDismiss={onDismiss}
      isErrorModal
      amplitudeEventData={{
        modalAppearanceEventMessage: message,
        modalMessage: typeof modalBody === 'string' ? modalBody : undefined,
      }}
      backgroundColor={theme.token('background-default')}
      footer={isShowingOrderingIssue && <OrderIssues />}
    >
      <ModalContent alignItems="center" paddingX="$8" testID="cart-error-modal-content">
        <HeadingContainer modalHeading={modalHeading} />
        <Body>{modalBody}</Body>
        {disclaimer && <Disclaimer>{disclaimer}</Disclaimer>}
        <ButtonGutter>
          {onAccept && (
            <LinkWrapper>
              <ActionLink onPress={onAccept} to={'.'} fullWidth>
                {t('accept')}
              </ActionLink>
            </LinkWrapper>
          )}
          <LinkWrapper>
            <ActionButton onPress={startOverCallback} testID="cart-error-start-over" fullWidth>
              {t('restartPurchaseProcessButton')}
            </ActionButton>
          </LinkWrapper>
        </ButtonGutter>
      </ModalContent>
    </Modal>
  );
};

export default CartError;
